import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import MovieItem from '../components/MovieItem';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// Define the styles for the Genre page
const styles = `
  /* Horizontal scroll container */
  .genre-movies-container {
    position: relative;
  }
  .genre-movies-list {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    padding: 0.5rem 0;
  }
  .genre-movies-list::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  .genre-movies-list > div {
    flex: 0 0 auto;
    scroll-snap-align: start;
    margin-right: 0.75rem; /* Reduced spacing between items */
  }
  /* Movie item wrapper for hover effects */
  .movie-item-wrapper {
    position: relative;
    transition: transform 0.3s ease;
    width: 160px; /* Fixed width for consistency */
  }
  .movie-item-wrapper:hover {
    transform: scale(1.05);
  }
  /* Scroll buttons */
  .scroll-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.7);
    color: #FF1493;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.3s ease, color 0.3s ease;
    z-index: 10;
  }
  .scroll-btn:hover {
    background: #FF1493;
    color: white;
  }
  .scroll-btn.left {
    left: -20px;
  }
  .scroll-btn.right {
    right: -20px;
  }
  .scroll-btn:disabled {
    background: rgba(0, 0, 0, 0.3);
    color: #666;
    cursor: not-allowed;
  }
  /* View All button */
  .view-all-btn {
    border: 2px solid #FF1493;
    color: #FF1493;
    background: transparent;
    padding: 0.5rem 1.5rem;
    border-radius: 9999px;
    font-weight: 600;
    transition: all 0.3s ease;
  }
  .view-all-btn:hover {
    background: #FF1493;
    color: white;
    box-shadow: 0 0 15px rgba(255, 20, 147, 0.5);
  }
  /* Genre section styling */
  .genre-section {
    background: #1a1a1a;
    border-radius: 1rem;
    padding: 1.5rem;
    margin-bottom: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
  .genre-title {
    color: #FF1493;
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  /* Responsive adjustments */
  @media (max-width: 640px) {
    .genre-movies-list > div {
      margin-right: 0.5rem; /* Even closer spacing on mobile */
    }
    .movie-item-wrapper {
      width: 140px; /* Slightly smaller on mobile */
    }
    .genre-title {
      font-size: 1.5rem;
    }
    .view-all-btn {
      padding: 0.4rem 1rem;
      font-size: 0.875rem;
    }
    .scroll-btn {
      width: 32px; /* Smaller buttons on mobile */
      height: 32px;
      font-size: 0.875rem; /* Smaller icon */
    }
    .scroll-btn.left {
      left: -10px; /* Closer to the edge on mobile */
    }
    .scroll-btn.right {
      right: -10px; /* Closer to the edge on mobile */
    }
  }
  @media (min-width: 1024px) {
    .movie-item-wrapper {
      width: 180px; /* Slightly larger on desktop, but not oversized */
    }
  }
`;

// Inject the styles into the document
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default function Genre() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [genresData, setGenresData] = useState({});
  const [scrollPositions, setScrollPositions] = useState({});

  // Define the genres (same as in UploadMovie)
  const genreOptions = [
    "Action", "Horror", "Series", "Adventure", "Love Story", "Comedy", "Crime",
    "Family", "Sci-Fi", "Romance", "Kungu-Fu", "Drama", "Sport", "Thriller",
    "Animation", "Documentaries", "NollyWood", "BollyWood", "Ugandan", "Kenyan", 
    "Luganda-Translated", "Trailers"
  ];

  // Refs for each genre's scroll container
  const scrollRefs = useRef({});

  // Initialize refs and scroll positions for each genre
  useEffect(() => {
    genreOptions.forEach((genre) => {
      scrollRefs.current[genre] = React.createRef();
      setScrollPositions((prev) => ({ ...prev, [genre]: { left: 0, canScrollLeft: false, canScrollRight: false } }));
    });
  }, []);

  // Fetch the 10 latest movies for each genre
  useEffect(() => {
    async function fetchMoviesByGenre() {
      setLoading(true);
      const data = {};

      try {
        for (const genre of genreOptions) {
          const moviesQuery = query(
            collection(db, "movies"),
            where("genre", "==", genre),
            orderBy("timestamp", "desc"),
            limit(10)
          );
          const moviesSnap = await getDocs(moviesQuery);
          data[genre] = moviesSnap.docs.map(doc => ({
            id: doc.id,
            data: doc.data()
          }));
        }
        setGenresData(data);
      } catch (error) {
        toast.error("Failed to load movies");
        console.error("Error fetching movies:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchMoviesByGenre();
  }, []);

  // Update scroll positions and button states
  useEffect(() => {
    const handleScroll = (genre) => {
      const container = scrollRefs.current[genre]?.current;
      if (container) {
        const canScrollLeft = container.scrollLeft > 0;
        const canScrollRight = container.scrollLeft < container.scrollWidth - container.clientWidth - 1;
        setScrollPositions((prev) => ({
          ...prev,
          [genre]: {
            left: container.scrollLeft,
            canScrollLeft,
            canScrollRight
          }
        }));
      }
    };

    genreOptions.forEach((genre) => {
      const container = scrollRefs.current[genre]?.current;
      if (container) {
        container.addEventListener('scroll', () => handleScroll(genre));
        // Initial check
        handleScroll(genre);
      }
    });

    // Cleanup event listeners
    return () => {
      genreOptions.forEach((genre) => {
        const container = scrollRefs.current[genre]?.current;
        if (container) {
          container.removeEventListener('scroll', () => handleScroll(genre));
        }
      });
    };
  }, [genresData]);

  // Handle "View All" navigation
  function handleViewAll(genre) {
    navigate(`/genre/${genre.toLowerCase()}`);
  }

  // Handle scroll left
  function handleScrollLeft(genre) {
    const container = scrollRefs.current[genre]?.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.8; // Scroll by 80% of the container width
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  }

  // Handle scroll right
  function handleScrollRight(genre) {
    const container = scrollRefs.current[genre]?.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.8; // Scroll by 80% of the container width
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="min-h-screen bg-black text-white py-3 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-[#FF1493] mb-6 text-center">
          Explore by Genre
        </h1>

        {genreOptions.map((genre) => (
          <div key={genre} className="genre-section">
            {/* Genre Title and View All Button */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="genre-title">{genre}</h2>
              {genresData[genre]?.length > 0 && (
                <button
                  onClick={() => handleViewAll(genre)}
                  className="view-all-btn"
                >
                  View All
                </button>
              )}
            </div>

            {/* Horizontal List of Movies with Scroll Buttons */}
            {genresData[genre]?.length > 0 ? (
              <div className="genre-movies-container">
                <button
                  onClick={() => handleScrollLeft(genre)}
                  className="scroll-btn left"
                  disabled={!scrollPositions[genre]?.canScrollLeft}
                >
                  <FaChevronLeft />
                </button>
                <div
                  className="genre-movies-list"
                  ref={scrollRefs.current[genre]}
                >
                  {genresData[genre].map((movie) => (
                    <div key={movie.id} className="movie-item-wrapper">
                      <MovieItem
                        movie={{ ...movie.data, id: movie.id }}
                        onClick={() => navigate(`/movie/${movie.id}`)}
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => handleScrollRight(genre)}
                  className="scroll-btn right"
                  disabled={!scrollPositions[genre]?.canScrollRight}
                >
                  <FaChevronRight />
                </button>
              </div>
            ) : (
              <p className="text-gray-400 text-center py-6">
                No movies available in this genre yet.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}